import { SUPER_ADMIN_BASE_SERVICE } from '../../utils/ApiUrls';

const superAdminAttachmentService = {
    /**
     * Download an audit question attachment (for superadmin)
     * @param {string} token - JWT access token
     * @param {number} attachmentId - ID of the attachment to download
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    downloadAttachment: async (token, attachmentId) => {
        try {
            const response = await fetch(
                `${SUPER_ADMIN_BASE_SERVICE}/attachments/${attachmentId}/download/`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                return {
                    success: false,
                    error: errorData.detail || 'Failed to download attachment'
                };
            }

            // Get the filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `attachment_${attachmentId}`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            // Get the blob
            const blob = await response.blob();
            
            // Create a download link and trigger it
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Error downloading attachment:', error);
            return {
                success: false,
                error: error.message || 'An error occurred while downloading the attachment'
            };
        }
    },

    /**
     * Download a followup attachment (for superadmin)
     * @param {string} token - JWT access token
     * @param {number} attachmentId - ID of the followup attachment to download
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    downloadFollowupAttachment: async (token, attachmentId) => {
        try {
            const response = await fetch(
                `${SUPER_ADMIN_BASE_SERVICE}/followup-attachments/${attachmentId}/download/`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                return {
                    success: false,
                    error: errorData.detail || 'Failed to download attachment'
                };
            }

            // Get the filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `followup_attachment_${attachmentId}`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            // Get the blob
            const blob = await response.blob();
            
            // Create a download link and trigger it
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Error downloading followup attachment:', error);
            return {
                success: false,
                error: error.message || 'An error occurred while downloading the attachment'
            };
        }
    },

    /**
     * Download an audit plan attachment (for superadmin)
     * @param {string} token - JWT access token
     * @param {number} attachmentId - ID of the audit plan attachment to download
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    downloadAuditPlanAttachment: async (token, attachmentId) => {
        try {
            const response = await fetch(
                `${SUPER_ADMIN_BASE_SERVICE}/plan-attachments/${attachmentId}/download/`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                return {
                    success: false,
                    error: errorData.error || 'Failed to download attachment'
                };
            }

            // Get the filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `plan_attachment_${attachmentId}`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            // Get the blob
            const blob = await response.blob();
            
            // Create a download link and trigger it
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Error downloading audit plan attachment:', error);
            return {
                success: false,
                error: error.message || 'An error occurred while downloading the attachment'
            };
        }
    }
};

export default superAdminAttachmentService;